import { useCallback, useEffect, useRef, useState } from 'react'
import { BookmarkCheck, FileText, Info, Scale, WifiOff } from 'lucide-react'
import { About } from './components/About'
import { Home } from './components/Home'
import { LegalHelp } from './components/LegalHelp'
import { PagePicker } from './components/PagePicker'
import { Preview } from './components/Preview'
import { Processing, type Step } from './components/Processing'
import { Result } from './components/Result'
import { SavedView } from './components/SavedView'
import { useSpeech } from './hooks/useSpeech'
import { isSupportedLanguage, ocrModelsFor } from './i18n/languages'
import { pack, type LanguagePack } from './i18n/packs'
import { t } from './i18n/translations'
import { extractDocument, hasReadableText } from './services/extract'
import {
  assessQuality,
  cropCanvas,
  findPaperBounds,
  loadImage,
  MAX_PDF_PAGES,
  MAX_PDF_TOTAL_PAGES,
  openPdf,
  preprocessForOcr,
  readPdf,
  recognizePages,
  releaseOcr,
  renderPdfPage,
  rotateCanvas,
  validateFile,
  type OcrProgress,
  type QualityReport
} from './services/ocr'
import { clearSpeechMemo } from './services/speech'
import { clearSaved, deleteSaved, listSaved, saveDocument } from './services/storage'
import { papersNeedingCheckIn } from './services/tracker'
import { applyTheme, readSetting, readTheme, writeSetting, type ThemePref } from './services/settings'
import type { AppSettings } from './components/About'
import type { FileKind, Language, OcrResult, SavedDocument, StructuredDocument } from './types/document'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type PdfDocument = Awaited<ReturnType<typeof openPdf>>
type ErrorKey = 'errBadImage' | 'errNoText' | 'errFileType' | 'errFileSize' | 'errGeneric' | 'errOcrDownload' | 'errLongDocument' | 'errUnsupportedLanguage'

type Stage =
  | { name: 'home'; error?: ErrorKey }
  | { name: 'preview'; image: HTMLCanvasElement; quality: QualityReport }
  | { name: 'pages'; pdf: PdfDocument; thumbnails: string[] }
  | { name: 'processing'; step: Step; progress: number | null; downloading: boolean }
  | {
      name: 'result'
      doc: StructuredDocument
      ocr: OcrResult | null
      fileKind: FileKind
      image?: HTMLCanvasElement
      savedId?: string
    }

const LANGUAGE_KEY = 'samjho-language'

function readStoredLanguage(): { language: Language; unsupported: boolean } {
  let requested: string | null = null
  try {
    requested = new URLSearchParams(window.location.search).get('lang') ?? localStorage.getItem(LANGUAGE_KEY)
  } catch {
    // storage unavailable (private mode) — fall through to defaults
  }
  if (requested && isSupportedLanguage(requested)) return { language: requested, unsupported: false }
  const fromBrowser = navigator.languages?.map((l) => l.split('-')[0]).find(isSupportedLanguage)
  return { language: fromBrowser ?? 'hi', unsupported: Boolean(requested) }
}

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)))

function App() {
  const [initial] = useState(readStoredLanguage)
  const [language, setLanguage] = useState<Language>(initial.language)
  const [docLanguage, setDocLanguage] = useState<Language>(initial.language === 'en' ? 'hi' : initial.language)
  // ?view=legal opens Kanooni Madad directly (links from the static legal pages)
  const [view, setView] = useState<'flow' | 'saved' | 'about' | 'legal'>(() =>
    new URLSearchParams(window.location.search).get('view') === 'legal' ? 'legal' : 'flow'
  )
  const [stage, setStage] = useState<Stage>({ name: 'home', error: initial.unsupported ? 'errUnsupportedLanguage' : undefined })
  const [saved, setSaved] = useState<SavedDocument[]>([])
  const [theme, setTheme] = useState<ThemePref>(readTheme)
  const [settings, setSettings] = useState<AppSettings>(() => ({ helperMode: readSetting('helperMode'), listenMode: readSetting('listenMode') }))
  const [offline, setOffline] = useState(!navigator.onLine)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [toast, setToast] = useState('')

  const cameraInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const runRef = useRef(0)
  const speech = useSpeech()
  const p: LanguagePack = pack(language)
  const copy = t(language)

  useEffect(() => {
    try {
      localStorage.setItem(LANGUAGE_KEY, language)
    } catch {
      // ignore
    }
    document.documentElement.lang = language
  }, [language])

  useEffect(() => {
    listSaved().then(setSaved).catch(() => setSaved([]))
    const online = () => setOffline(false)
    const offlineEvent = () => setOffline(true)
    const installHandler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('online', online)
    window.addEventListener('offline', offlineEvent)
    window.addEventListener('beforeinstallprompt', installHandler)
    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offlineEvent)
      window.removeEventListener('beforeinstallprompt', installHandler)
    }
  }, [])

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(''), 2500)
    return () => clearTimeout(timer)
  }, [toast])

  function changeLanguage(next: Language) {
    speech.stop()
    setLanguage(next)
    if (next !== 'en') setDocLanguage(next)
  }

  /** Drops every reference to the current document (image, text, results). */
  const reset = useCallback((error?: ErrorKey) => {
    runRef.current += 1
    speech.stop()
    setStage({ name: 'home', error })
    setView('flow')
    if (cameraInputRef.current) cameraInputRef.current.value = ''
    if (fileInputRef.current) fileInputRef.current.value = ''
    void releaseOcr()
    clearSpeechMemo()
  }, [speech])

  // ── Pipeline ────────────────────────────────────────────────────────────────

  function progressHandler(run: number, step: Step) {
    return (progress: OcrProgress) => {
      if (run !== runRef.current) return
      const pageShare = progress.pages ? ((progress.page ?? 1) - 1 + progress.progress) / progress.pages : progress.progress
      setStage({
        name: 'processing',
        step,
        progress: progress.stage === 'download' ? progress.progress : pageShare,
        downloading: progress.stage === 'download'
      })
    }
  }

  async function finish(run: number, ocr: OcrResult, fileKind: FileKind, image?: HTMLCanvasElement) {
    if (run !== runRef.current) return
    setStage({ name: 'processing', step: 3, progress: null, downloading: false })
    await nextFrame()
    if (!hasReadableText(ocr)) {
      const almostNothing = ocr.text.replace(/\s/g, '').length < 10
      setStage({ name: 'home', error: almostNothing ? 'errNoText' : 'errBadImage' })
      return
    }
    const doc = extractDocument(ocr)
    if (run !== runRef.current) return
    setStage({ name: 'processing', step: 4, progress: null, downloading: false })
    await nextFrame()
    if (run !== runRef.current) return
    setStage({ name: 'result', doc, ocr, fileKind, image })
    window.scrollTo({ top: 0 })
  }

  function failed(run: number, error: unknown) {
    if (run !== runRef.current) return
    const message = error instanceof Error ? error.message : String(error)
    const network = !navigator.onLine || /fetch|network|load|traineddata|importScripts/i.test(message)
    setStage({ name: 'home', error: network ? 'errOcrDownload' : 'errGeneric' })
  }

  async function readImage(image: HTMLCanvasElement) {
    const run = ++runRef.current
    speech.stop()
    setStage({ name: 'processing', step: 1, progress: null, downloading: false })
    try {
      await nextFrame()
      const paper = findPaperBounds(image)
      const prepared = preprocessForOcr(paper ? cropCanvas(image, paper) : image)
      if (run !== runRef.current) return
      setStage({ name: 'processing', step: 2, progress: 0, downloading: false })
      const ocr = await recognizePages([prepared], ocrModelsFor(docLanguage), progressHandler(run, 2))
      await finish(run, ocr, 'image', image)
    } catch (error) {
      failed(run, error)
    }
  }

  async function readPdfPages(pdf: PdfDocument, pages: number[]) {
    const run = ++runRef.current
    setStage({ name: 'processing', step: 2, progress: 0, downloading: false })
    try {
      const ocr = await readPdf(pdf, pages, ocrModelsFor(docLanguage), progressHandler(run, 2))
      await finish(run, ocr, 'pdf')
    } catch (error) {
      failed(run, error)
    }
  }

  async function handleFile(file?: File) {
    if (!file) return
    const problem = validateFile(file)
    if (problem) {
      setStage({ name: 'home', error: problem === 'type' ? 'errFileType' : 'errFileSize' })
      return
    }
    const run = ++runRef.current
    setView('flow')
    setStage({ name: 'processing', step: 1, progress: null, downloading: false })
    try {
      if (file.type === 'application/pdf') {
        const pdf = await openPdf(file)
        if (run !== runRef.current) return
        if (pdf.numPages > MAX_PDF_TOTAL_PAGES) {
          setStage({ name: 'home', error: 'errLongDocument' })
          return
        }
        if (pdf.numPages <= MAX_PDF_PAGES) {
          await readPdfPages(pdf, Array.from({ length: pdf.numPages }, (_, i) => i + 1))
          return
        }
        // Long PDF: ask which pages matter instead of silently processing everything
        const thumbnails: string[] = []
        for (let page = 1; page <= pdf.numPages; page += 1) {
          const canvas = await renderPdfPage(pdf, page, 0.35)
          thumbnails.push(canvas.toDataURL('image/jpeg', 0.6))
          if (run !== runRef.current) return
        }
        setStage({ name: 'pages', pdf, thumbnails })
        return
      }
      const image = await loadImage(file)
      if (run !== runRef.current) return
      setStage({ name: 'preview', image, quality: assessQuality(image) })
    } catch {
      if (run === runRef.current) setStage({ name: 'home', error: 'errBadImage' })
    }
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (stage.name !== 'result' || stage.savedId) return
    const item: SavedDocument = { id: crypto.randomUUID(), savedAt: new Date().toISOString(), fileKind: stage.fileKind, doc: stage.doc }
    await saveDocument(item)
    setStage({ ...stage, savedId: item.id })
    setSaved(await listSaved())
  }

  async function handleShare(text: string) {
    // Shared through the phone's share sheet or clipboard — never placed in a URL
    try {
      if (navigator.share) {
        await navigator.share({ text })
        return
      }
      await navigator.clipboard.writeText(text)
      setToast(p.copied)
    } catch {
      // user cancelled the share sheet
    }
  }

  function shareApp() {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(copy.shareAppMessage)}`, '_blank', 'noopener')
  }

  async function installPwa() {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    if (choice.outcome === 'accepted') setDeferredPrompt(null)
  }

  const resultImage = stage.name === 'result' ? stage.image : undefined

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => reset()} aria-label={p.newDocument}>
          <span className="brand-mark" aria-hidden="true">स</span>
          <span>
            <strong>{copy.appName}</strong>
            <small>{copy.tagline}</small>
          </span>
        </button>
      </header>

      {offline && (
        <div className="offline-banner">
          <WifiOff size={17} aria-hidden="true" />
          <span>
            <strong>{copy.offline}.</strong> {copy.offlineHint}
          </span>
        </div>
      )}

      {/* Hidden inputs: camera opens the back camera directly on phones */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
      <input ref={fileInputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" hidden onChange={(e) => handleFile(e.target.files?.[0])} />

      <main className="page">
        {view === 'flow' && stage.name === 'home' && papersNeedingCheckIn(saved).length > 0 && (
          <button className="notice-box checkin-banner" onClick={() => setView('saved')}>
            <span aria-hidden="true">📅</span>
            <span>{copy.deadlineCheckIn.replace('{n}', String(papersNeedingCheckIn(saved).length))}</span>
          </button>
        )}
        {view === 'flow' && stage.name === 'home' && (
          <Home
            p={p}
            copy={copy}
            language={language}
            onLanguage={changeLanguage}
            error={stage.error ? p[stage.error] : ''}
            onTakePhoto={() => cameraInputRef.current?.click()}
            onUpload={() => fileInputRef.current?.click()}
            onShareApp={shareApp}
            onInstall={deferredPrompt ? installPwa : undefined}
            onForOrgs={() => window.location.assign('/for-organisations/')}
          />
        )}
        {view === 'flow' && stage.name === 'preview' && (
          <Preview
            p={p}
            image={stage.image}
            quality={stage.quality}
            docLanguage={docLanguage}
            onDocLanguage={setDocLanguage}
            onRead={readImage}
            onRetake={() => {
              reset()
              cameraInputRef.current?.click()
            }}
          />
        )}
        {view === 'flow' && stage.name === 'pages' && (
          <PagePicker p={p} thumbnails={stage.thumbnails} onRead={(pages) => readPdfPages(stage.pdf, pages)} />
        )}
        {view === 'flow' && stage.name === 'processing' && (
          <Processing p={p} step={stage.step} progress={stage.progress} downloading={stage.downloading} onCancel={() => reset()} />
        )}
        {view === 'flow' && stage.name === 'result' && (
          <Result
            p={p}
            language={language}
            onLanguage={changeLanguage}
            doc={stage.doc}
            ocr={stage.ocr}
            speech={speech}
            saved={Boolean(stage.savedId)}
            onSave={handleSave}
            onShare={handleShare}
            onNew={() => reset()}
            onRetryRotated={resultImage ? () => readImage(rotateCanvas(resultImage, 1)) : undefined}
            helperMode={settings.helperMode}
            listenMode={settings.listenMode}
            onOpenLegal={() => {
              speech.stop()
              setView('legal')
              window.scrollTo({ top: 0 })
            }}
          />
        )}
        {view === 'saved' && (
          <SavedView
            copy={copy}
            p={p}
            language={language}
            items={saved}
            onOpen={(item) => {
              speech.stop()
              setStage({ name: 'result', doc: item.doc, ocr: null, fileKind: item.fileKind, savedId: item.id })
              setView('flow')
            }}
            onDelete={async (id) => {
              await deleteSaved(id)
              if (stage.name === 'result' && stage.savedId === id) setStage({ ...stage, savedId: undefined })
              setSaved(await listSaved())
            }}
            onToggleDone={async (item) => {
              await saveDocument({ ...item, doneAt: item.doneAt ? undefined : new Date().toISOString() })
              setSaved(await listSaved())
            }}
            onClear={async () => {
              await clearSaved()
              if (stage.name === 'result') setStage({ ...stage, savedId: undefined })
              setSaved([])
            }}
          />
        )}
        {view === 'about' && (
          <About
            copy={copy}
            p={p}
            settings={settings}
            onSetting={(key, on) => {
              writeSetting(key, on)
              setSettings((current) => ({ ...current, [key]: on }))
            }}
            onShareApp={shareApp}
            language={language}
            theme={theme}
            onTheme={(t) => {
              applyTheme(t)
              setTheme(t)
            }}
          />
        )}
        {view === 'legal' && <LegalHelp p={p} language={language} onLanguage={changeLanguage} speech={speech} onShare={handleShare} />}
      </main>

      {toast && <div className="toast" role="status">{toast}</div>}

      <nav className="bottom-nav" aria-label="Main navigation">
        <button className={view === 'flow' ? 'active' : ''} onClick={() => (view === 'flow' ? reset() : setView('flow'))}>
          <FileText size={22} aria-hidden="true" />
          <span>{p.newDocument}</span>
        </button>
        <button
          className={view === 'legal' ? 'active' : ''}
          onClick={() => {
            speech.stop()
            setView('legal')
            window.scrollTo({ top: 0 })
          }}
        >
          <Scale size={22} aria-hidden="true" />
          <span>{p.navLegal}</span>
        </button>
        <button className={view === 'saved' ? 'active' : ''} onClick={() => setView('saved')}>
          <BookmarkCheck size={22} aria-hidden="true" />
          <span>{p.navSaved}</span>
        </button>
        <button className={view === 'about' ? 'active' : ''} onClick={() => setView('about')}>
          <Info size={22} aria-hidden="true" />
          <span>{p.navInfo}</span>
        </button>
      </nav>
    </div>
  )
}

export default App
